import { MdPerson, MdGroup, MdBusiness } from "react-icons/md";
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok, FaXTwitter } from "react-icons/fa6";

/**
 * Detects the file type from a URL based on its extension.
 * @param {string} url - The file URL to check
 * @returns {string} "image", "pdf", or "unknown"
 */
function detectFileType(url) {
    if (!url) return "unknown";
    const extension = url.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension) ? "image" : "pdf";
}

/**
 * Returns type metadata (label, icon, color) based on the certification's owner.
 * @param {Object} cert - The certification object
 * @returns {Object} Type config with type, label, icon component, and color
 */
function getCertificationType(cert) {
    if (cert.user_id) return { type: 'user', label: 'Artiste', icon: MdPerson, color: '#2196f3' };
    if (cert.artist_group_id) return { type: 'group', label: 'Groupe', icon: MdGroup, color: '#9c27b0' };
    if (cert.entity_id) return { type: 'entity', label: 'Établissement', icon: MdBusiness, color: '#ff9800' };
    return { type: 'unknown', label: 'Inconnu', icon: MdPerson, color: '#9e9e9e' };
}

/**
 * Returns status display configuration based on certification status string.
 * @param {string} status - Certification status key
 * @returns {Object} Config with label, color, and background color
 */
function getStatusConfig(status) {
    const configs = {
        accepted: { label: 'Compte certifié', color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.1)' },
        pending: { label: 'En attente', color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.1)' },
        in_verification: { label: 'En cours de vérification', color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.1)' },
        declined: { label: 'Demande déclinée', color: '#f44336', bgColor: 'rgba(244, 67, 54, 0.1)' }
    };
    return configs[status] || { label: 'Statut inconnu', color: '#9e9e9e', bgColor: 'rgba(158, 158, 158, 0.1)' };
}

/**
 * Extracts social media links from a certification object.
 * @param {Object} cert - The certification object with social link fields
 * @returns {Array} Array of objects with name, url, icon, and color
 */
function getSocialLinks(cert) {
    const links = [];
    if (cert.facebook_link) links.push({ name: 'Facebook', url: cert.facebook_link, icon: FaFacebook, color: '#1877f2' });
    if (cert.instagram_link) links.push({ name: 'Instagram', url: cert.instagram_link, icon: FaInstagram, color: '#e4405f' });
    if (cert.tik_tok_link) links.push({ name: 'TikTok', url: cert.tik_tok_link, icon: FaTiktok, color: '#000000' });
    if (cert.x_twitter_link) links.push({ name: 'X (Twitter)', url: cert.x_twitter_link, icon: FaXTwitter, color: '#000000' });
    if (cert.youtube_link) links.push({ name: 'YouTube', url: cert.youtube_link, icon: FaYoutube, color: '#ff0000' });
    return links;
}

const DOCUMENT_ROLE_LABELS = {
    identity_front: { label: "Recto identité",       color: "#2196f3" },
    identity_back:  { label: "Verso identité",       color: "#2196f3" },
    address_proof:  { label: "Justificatif domicile", color: "#9c27b0" }
};

/**
 * Returns the display label and color for a KYC document role.
 * @param {string|null|undefined} role - One of 'identity_front', 'identity_back', 'address_proof', or null
 * @returns {{label:string,color:string}|null} Role descriptor, or null for legacy / unknown roles
 */
function getDocumentRoleLabel(role) {
    if (!role) return null;
    return DOCUMENT_ROLE_LABELS[role] || null;
}

export { detectFileType, getCertificationType, getStatusConfig, getSocialLinks, getDocumentRoleLabel };
